/**
 * Bundle loader utility for loading and executing microfrontend bundles
 */

interface BundleInterface {
  mount: (containerId: string) => void;
  unmount: () => void;
}

interface LoadedBundle {
  bundle: BundleInterface;
  scriptElement: HTMLScriptElement;
  styleElement?: HTMLLinkElement;
}

const loadedBundles = new Map<string, LoadedBundle>();

// Session-based cache buster - consistent within a session, changes on page reload
// This allows browser caching to work while still busting cache on new sessions
const SESSION_VERSION = Date.now();

/**
 * Load a bundle from a URL and execute it
 * @param appName - Name of the app (e.g., 'chat', 'weather')
 * @param bundleUrl - URL to the bundle (e.g., 'https://room-chat.pages.dev/bundle/app.js')
 * @param retryCount - Internal parameter for retry logic
 * @returns Promise that resolves to the bundle interface
 */
export async function loadBundle(
  appName: string,
  bundleUrl: string,
  retryCount = 0
): Promise<BundleInterface> {
  console.log(`[BundleLoader] Loading bundle for ${appName} from ${bundleUrl}`);

  // TEMPORARY: Disable memory cache to ensure fresh bundles during development
  // TODO: Re-enable with proper version-based invalidation
  // const cached = loadedBundles.get(appName);
  // if (cached) {
  //   return cached.bundle;
  // }

  // Always unload existing bundle before loading new one
  unloadBundle(appName);

  // Load CSS first
  // Handle query string properly: /app.js?v=123 → /app.css?v=123
  const cssUrl = bundleUrl.replace(/\.js(\?.*)?$/, '.css$1');
  console.log(`[BundleLoader] Loading CSS from ${cssUrl}`);

  const styleElement = document.createElement('link');
  styleElement.rel = 'stylesheet';
  styleElement.href = cssUrl;
  document.head.appendChild(styleElement);

  const cssLoadPromise = new Promise<void>((resolve, reject) => {
    styleElement.onload = () => {
      console.log(`[BundleLoader] CSS loaded successfully: ${cssUrl}`);
      resolve();
    };
    styleElement.onerror = (event) => {
      // CSS is optional - don't show error for missing CSS files
      console.log(`[BundleLoader] CSS not available (optional): ${cssUrl}`);
      resolve();
    };
  });

  // Load JS bundle
  console.log(`[BundleLoader] Loading JS from ${bundleUrl}`);
  const scriptElement = document.createElement('script');
  scriptElement.src = bundleUrl;
  scriptElement.type = 'text/javascript';

  const loadPromise = new Promise<BundleInterface>((resolve, reject) => {
    scriptElement.onload = () => {
      console.log(`[BundleLoader] Script loaded, looking for bundle interface...`);

      // Use generic namespace for all apps
      const bundle = (window as any).RoomAppBundle;
      console.log(`[BundleLoader] Checking RoomAppBundle:`, bundle ? 'found' : 'not found');

      if (!bundle || typeof bundle.mount !== 'function') {
        const error = new Error(
          `Bundle not found or invalid for app: ${appName}. ` +
          `Expected window.RoomAppBundle to exist with mount() function.`
        );
        console.error(`[BundleLoader] ${error.message}`);
        console.log('[BundleLoader] Available globals:', Object.keys(window).filter(k => k.startsWith('Room')));
        reject(error);
        return;
      }

      // Cache the loaded bundle
      loadedBundles.set(appName, {
        bundle,
        scriptElement,
        styleElement,
      });

      console.log(`[BundleLoader] Bundle loaded and cached successfully for ${appName}`);
      resolve(bundle);
    };

    scriptElement.onerror = async (event) => {
      // Retry logic: Try without cache buster on 404
      if (retryCount === 0 && bundleUrl.includes('?v=')) {
        console.log(`[BundleLoader] Retrying without cache buster...`);
        scriptElement.remove();
        styleElement.remove();
        try {
          const retryUrl = bundleUrl.split('?')[0]; // Remove query params
          const result = await loadBundle(appName, retryUrl, retryCount + 1);
          resolve(result);
          return;
        } catch (retryError) {
          // Fall through to error
        }
      }

      const error = new Error(
        `Failed to load bundle script from: ${bundleUrl}. ` +
        `This could be due to: network error, CORS issue, file not found (404), or R2 bucket access problem.`
      );
      console.error(`[BundleLoader] ${error.message}`, event);
      reject(error);
    };
  });

  document.head.appendChild(scriptElement);

  await cssLoadPromise;
  return loadPromise;
}

/**
 * Unload a bundle and clean up resources
 * @param appName - Name of the app
 */
export function unloadBundle(appName: string): void {
  const loaded = loadedBundles.get(appName);
  if (!loaded) {
    return;
  }

  // Unmount the bundle
  loaded.bundle.unmount();

  // Remove script and style elements
  loaded.scriptElement.remove();
  loaded.styleElement?.remove();

  // Remove from cache
  loadedBundles.delete(appName);
}

/**
 * Check if a bundle is loaded
 * @param appName - Name of the app
 */
export function isBundleLoaded(appName: string): boolean {
  return loadedBundles.has(appName);
}

/**
 * Get bundle URL from app name
 * Bundles are served via Worker API (proxying to R2).
 *
 * @param appName - Name of the app (e.g., 'chat', 'weather')
 * @returns Bundle URL with cache busting timestamp
 */
export function getBundleUrl(appName: string): string {
  // Detect current environment
  const currentHost = window.location.host;

  // Use session-based cache buster - consistent within session for better caching
  const cacheBuster = `?v=${SESSION_VERSION}`;

  // Local development - use local bundle
  if (currentHost.includes('localhost') || currentHost.includes('127.0.0.1')) {
    return `http://localhost:5173/bundle/app.js${cacheBuster}`;
  }

  // Production: Use worker API (same origin) to serve bundles from R2
  return `/api/bundles/${appName}/app.js${cacheBuster}`;
}

/**
 * Capitalize first letter of string
 */
function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
